import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { defaultNavItems } from '../../config/defaultData';
import * as navService from '../../services/navService';
import logger from '../../utils/logger';
import { setAllWebsites } from './websiteSlice';
import storage from '../../utils/LocalStorageManager';

// 从后端API获取管理员默认导航数据（公开接口）
export const fetchAdminDefaultNavItems = createAsyncThunk(
  'nav/fetchAdminDefaultNavItems',
  async (options = {}, { dispatch, rejectWithValue }) => {
    try {
      const { navItems, websitesByNav } = await navService.fetchAdminDefaultNavItems();
      
      // 更新网站数据
      if (websitesByNav) {
        dispatch(setAllWebsites(websitesByNav));
      }

      if (Array.isArray(navItems) && navItems.length > 0) {
        // 确保首页项目保持disableDrag=true
        return navItems.map(item => {
          if (item.id === 'home') {
            return {...item, disableDrag: true};
          }
          return item;
        });
      }
      return null;
    } catch (error) {
      logger.error('[Redux] 从API获取管理员默认导航菜单项失败:', error);
      return null;
    }
  }
);

// 从后端API获取导航项
export const fetchNavItemsFromApi = createAsyncThunk(
  'nav/fetchNavItemsFromApi',
  async (options = {}, { dispatch, rejectWithValue }) => {
    try {
      const { navItems, websitesByNav } = await navService.fetchNavItems();
      
      // 更新网站数据
      if (websitesByNav) {
        // 直接传递websitesByNav对象，不要包装
        dispatch(setAllWebsites(websitesByNav));
      }

      if (Array.isArray(navItems) && navItems.length > 0) {
        // 确保首页项目保持disableDrag=true
        return navItems.map(item => {
          if (item.id === 'home') {
            return {...item, disableDrag: true};
          }
          return item;
        });
      }
      return null;
    } catch (error) {
      logger.error('[Redux] 从API获取导航菜单项失败:', error);
      return null;
    }
  }
);

// 创建一个防抖函数来处理事件分发
const debounceDispatchEvent = (() => {
  const timers = {};
  return (eventName, detail = null, delay = 100) => {
    if (timers[eventName]) {
      clearTimeout(timers[eventName]);
    }
    timers[eventName] = setTimeout(() => {
      if (detail) {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
      } else {
        window.dispatchEvent(new Event(eventName));
      }
      delete timers[eventName];
    }, delay);
  };
})();

// 优化的导航项加载函数 - 本地数据优先 + 并行接口请求
export const loadNavItemsOptimized = createAsyncThunk(
  'nav/loadNavItemsOptimized',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      logger.log('【导航日志】loadNavItemsOptimized 开始执行...');
      
      // 1. 立即检查本地数据
      logger.log('【导航日志】检查本地存储数据...');
      const savedNavItems = storage.getNavItems();
      
      let localData = null;
      if (savedNavItems && Array.isArray(savedNavItems) && savedNavItems.length > 0) {
        logger.log('【导航日志】发现本地数据，数量:', savedNavItems.length);
        // 确保首页项目保持disableDrag=true
        localData = savedNavItems.map(item => {
          if (item.id === 'home') {
            return {...item, disableDrag: true};
          }
          return item;
        });
        logger.log('【导航日志】立即返回本地数据，同时启动后台API请求');
      } else {
        logger.log('【导航日志】本地数据不存在或为空');
      }
      
      // 2. 启动并行API请求（不阻塞返回）
      logger.log('【导航日志】启动后台API请求...');
      dispatch(fetchNavItemsFromApi())
        .unwrap()
        .then(apiResult => {
          if (apiResult && Array.isArray(apiResult) && apiResult.length > 0) {
            logger.log('【导航日志】后台API请求成功，更新数据，数量:', apiResult.length);
            // 直接更新Redux状态，触发UI刷新
            dispatch(setNavItems(apiResult));
            // 触发事件通知其他组件数据已更新
            debounceDispatchEvent('navItemsUpdated', { source: 'background-api' });
          } else {
            logger.log('【导航日志】后台API请求返回空数据');
          }
        })
        .catch(error => {
          logger.log('【导航日志】后台API请求失败:', error.message || error);
          // API失败不影响用户体验，静默处理
        });
      
      // 3. 决定立即返回的数据
      if (localData) {
        logger.log('【导航日志】返回本地数据，用户可立即使用');
        return localData;
      }
      
      // 4. 如果本地数据为空，且没有登录，则先请求管理员默认接口数据
      logger.log('【导航日志】本地数据不可用，尝试获取管理员默认数据');
      try {
        const adminDefaultResult = await dispatch(fetchAdminDefaultNavItems()).unwrap();
        if (adminDefaultResult && Array.isArray(adminDefaultResult) && adminDefaultResult.length > 0) {
          logger.log('【导航日志】成功获取管理员默认数据，数量:', adminDefaultResult.length);
           // 直接更新Redux状态，触发UI刷新
           dispatch(setNavItems(adminDefaultResult));
          return adminDefaultResult;
        }
      } catch (error) {
        logger.warn('【导航日志】获取管理员默认数据失败，将使用本地默认数据:', error);
      }
      
      // 5. 如果管理员默认数据也获取失败，使用本地默认数据作为兜底
      logger.log('【导航日志】使用本地默认数据兜底');
      const defaultItems = defaultNavItems.map(item => ({
        id: item.id,
        icon: item.icon,
        text: item.text,
        index: item.index,
        disableDrag: item.disableDrag
      }));
      logger.log('【导航日志】返回默认导航项，数量:', defaultItems.length);
      return defaultItems;
      
    } catch (error) {
      logger.error('【导航日志】loadNavItemsOptimized 执行失败:', error);
      return rejectWithValue('优化加载导航菜单项失败');
    }
  }
);

// 加载导航项，从API获取，如果失败则从storage获取
export const loadNavItems = createAsyncThunk(
  'nav/loadNavItems',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      logger.log('【导航日志】loadNavItems 开始执行...');
      
      // 1. 首先尝试从API获取数据
      logger.log('【导航日志】尝试从API获取导航菜单项...');
      const apiResult = await dispatch(fetchNavItemsFromApi()).unwrap();
      if (apiResult) {
        logger.log('【导航日志】成功从API加载导航菜单项:', apiResult);
        logger.log('【导航日志】API返回的导航项数量:', apiResult.length);
        return apiResult;
      }
      
      // 2. 如果API没有数据，则尝试从storage获取
      logger.log('【导航日志】API没有返回数据，尝试从storage加载');
      const savedNavItems = storage.getNavItems();
      
      if (savedNavItems && Array.isArray(savedNavItems) && savedNavItems.length > 0) {
        logger.log('【导航日志】从storage找到有效的导航项，数量:', savedNavItems.length);
        // 确保首页项目保持disableDrag=true
        const result = savedNavItems.map(item => {
          if (item.id === 'home') {
            return {...item, disableDrag: true};
          }
          return item;
        });
        logger.log('【导航日志】返回处理后的storage导航项');
        return result;
      }
      
      // 3. 如果没有保存的数据，使用默认值
      logger.log('【导航日志】本地存储中没有数据，使用默认导航菜单项');
      const defaultItems = defaultNavItems.map(item => ({
        id: item.id,
        icon: item.icon,
        text: item.text,
        index: item.index,
        disableDrag: item.disableDrag
      }));
      logger.log('【导航日志】返回默认导航项:', defaultItems);
      logger.log('【导航日志】默认导航项数量:', defaultItems.length);
      return defaultItems;
    } catch (error) {
      logger.error('【导航日志】加载导航菜单项失败:', error);
      return rejectWithValue('加载导航菜单项失败');
    }
  }
);

// 保存导航项到storage
const saveNavItemsToStorage = (items) => {
  try {
    logger.log('【导航日志】开始保存导航项到storage', items);
    if (storage.setNavItems(items)) {
      logger.log('【导航日志】导航项成功保存到storage, 数量:', items.length);
      return true;
    }
    return false;
  } catch (error) {
    logger.error('【导航日志】保存导航菜单项失败:', error);
    return false;
  }
};

// 同步导航项到后端API
const syncNavItemsToApi = async (items) => {
  try {
    logger.log('【导航日志】开始同步导航项到API:', items);
    const updatedItems = await navService.reorderNavItems(items);
    // 如果API返回了数据，并且是数组，则返回更新后的数组（包含服务端ID）
    if (Array.isArray(updatedItems) && updatedItems.length > 0) {
      logger.log('【导航日志】API返回的更新后导航项:', updatedItems);
      return updatedItems;
    }
    logger.log('【导航日志】API没有返回有效数据，使用原始导航项');
    return items; // 否则返回原始数组
  } catch (error) {
    logger.error('【导航日志】同步导航菜单项到API失败:', error);
    return items; // 失败时返回原始数组
  }
};

// 添加导航项（异步）
export const addNavItemAsync = createAsyncThunk(
  'nav/addNavItemAsync',
  async (newItem, { dispatch, getState, rejectWithValue }) => {
    try {
      // 先同步更新本地状态
      dispatch(addNavItem(newItem));
      
      let serverItem = null;
      // 同步到API
      try {
        serverItem = await navService.addNavItem(newItem);
        // 使用服务端返回的ID更新本地项
        if (serverItem && serverItem.id !== newItem.id) {
          logger.log(`使用服务端返回的ID ${serverItem.id} 更新本地导航菜单项`);
          
          // 更新本地状态
          dispatch(updateNavItem({ 
            id: newItem.id, 
            updates: { id: serverItem.id, ...serverItem } 
          }));
          
          // 触发自定义事件，通知其他组件ID已更新
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('navItemIdUpdated', {
              detail: {
                oldId: newItem.id,
                newId: serverItem.id
              }
            }));
          }, 0);
        }
      } catch (error) {
        logger.error('向API添加导航菜单项失败:', error);
      }
      
      return { newItem, serverItem };
    } catch (error) {
      logger.error(`添加导航项失败:`, error);
      return rejectWithValue(`添加导航项失败: ${error.message}`);
    }
  }
);

// 更新导航项（异步）
export const updateNavItemAsync = createAsyncThunk(
  'nav/updateNavItemAsync',
  async ({ id, updates }, { dispatch, rejectWithValue }) => {
    try {
      // 先同步更新本地状态
      dispatch(updateNavItem({ id, updates }));
      
      let serverItem = null;
      // 同步到API
      try {
        serverItem = await navService.updateNavItem(id, updates);
        if (serverItem && serverItem.id !== id) {
          logger.log('使用服务端返回的导航菜单项ID更新本地数据');
          
          // 更新本地状态
          dispatch(updateNavItem({ 
            id: id, 
            updates: { ...serverItem } 
          }));
        }
      } catch (error) {
        logger.error('向API更新导航菜单项失败:', error);
      }
      
      return { id, updates, serverItem };
    } catch (error) {
      logger.error(`更新导航项失败:`, error);
      return rejectWithValue(`更新导航项失败: ${error.message}`);
    }
  }
);

// 重排序导航项（异步）
export const reorderNavItemsAsync = createAsyncThunk(
  'nav/reorderNavItemsAsync',
  async ({ sourceIndex, targetIndex }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentItems = [...state.nav.navItems];
      const [removed] = currentItems.splice(sourceIndex, 1);
      currentItems.splice(targetIndex, 0, removed);
      
      // 更新索引
      const updatedItems = currentItems.map((item, index) => ({
        ...item,
        index
      }));
      
      // 先同步更新本地状态
      dispatch(setNavItems(updatedItems));
      
      // 同步到API并处理返回结果
      try {
        const serverItems = await syncNavItemsToApi(updatedItems);
        // 如果服务端返回了不同的数据，更新本地状态
        if (serverItems && JSON.stringify(serverItems) !== JSON.stringify(updatedItems)) {
          dispatch(setNavItems(serverItems));
          logger.log('[Redux] 使用服务端返回的排序结果更新本地导航菜单项');
          
          // 触发额外的事件通知ID可能已更新
          setTimeout(() => {
            window.dispatchEvent(new Event('navItemsReordered'));
          }, 0);
        }
      } catch (error) {
        logger.error('向API同步导航项排序失败:', error);
      }
      
      return { sourceIndex, targetIndex, updatedItems };
    } catch (error) {
      logger.error(`重排序导航项失败:`, error);
      return rejectWithValue(`重排序导航项失败: ${error.message}`);
    }
  }
);

// 删除导航项（异步）
export const deleteNavItemAsync = createAsyncThunk(
  'nav/deleteNavItemAsync',
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentItems = state.nav.navItems || [];
      const updatedItems = currentItems.filter(item => item.id !== id);
      
      // 更新导航项
      dispatch(setNavItems(updatedItems));
      
      // 删除对应的网站数据（本地存储）
      storage.removeWebsitesForNav(id);
      
      // 删除Redux中的网站数据
      const currentWebsitesByNav = state.website.websitesByNav || {};
      const updatedWebsitesByNav = { ...currentWebsitesByNav };
      delete updatedWebsitesByNav[id];
      console.log('jaydenxiaoupdatedWebsitesByNav', updatedWebsitesByNav);
      
      // 更新网站数据状态
      dispatch(setAllWebsites(updatedWebsitesByNav));
      
      // 同步到API
      try {
        await navService.deleteNavItem(id);
      } catch (error) {
        logger.error(`向API删除导航菜单项 ${id} 失败:`, error);
      }
      
      // 触发自定义事件，通知其他组件导航项已更新
      setTimeout(() => {
        window.dispatchEvent(new Event('navItemsUpdated'));
      }, 0);
      
      return { deletedId: id, updatedItems };
    } catch (error) {
      logger.error(`删除导航项 ${id} 失败:`, error);
      return rejectWithValue(`删除导航项失败: ${error.message}`);
    }
  }
);

const initialState = {
  navItems: [],
  loading: true,
  error: null,
};

const navSlice = createSlice({
  name: 'nav',
  initialState,
  reducers: {
    setNavItems: (state, action) => {
      state.navItems = action.payload;
      saveNavItemsToStorage(action.payload);
      
      // 触发自定义事件，通知其他组件导航项已更新
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('navItemsUpdated', {
          detail: {
            isFromSync: true
          }
        }));
      }, 0);
    },
    addNavItem: (state, action) => {
      const newItem = action.payload;
      const newNavItems = [...state.navItems, newItem];
      state.navItems = newNavItems;
      saveNavItemsToStorage(newNavItems);
      
      // 触发自定义事件，通知其他组件导航项已更新
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('navItemsUpdated', {
          detail: {
            isNewCategory: true,
            newCategoryId: newItem.id
          }
        }));
      }, 0);
    },
    updateNavItem: (state, action) => {
      const { id, updates } = action.payload;
      const currentItems = state.navItems || [];
      const updatedItems = currentItems.map(item => 
        item.id === id ? { ...item, ...updates } : item
      );
      
      // 更新 Redux 状态
      state.navItems = updatedItems;
      
      // 记录到本地存储
      saveNavItemsToStorage(updatedItems);
      
      // 触发自定义事件，通知其他组件导航项已更新
      setTimeout(() => {
        window.dispatchEvent(new Event('navItemsUpdated'));
      }, 0);
    },
    reorderNavItems: (state, action) => {
      const { sourceIndex, targetIndex } = action.payload;
      // 创建新数组防止直接修改状态
      const items = [...state.navItems];
      const [removed] = items.splice(sourceIndex, 1);
      items.splice(targetIndex, 0, removed);
      
      // 更新索引
      const updatedItems = items.map((item, index) => ({
        ...item,
        index
      }));
      
      state.navItems = updatedItems;
      saveNavItemsToStorage(updatedItems);
      
      // 触发自定义事件，通知其他组件导航项已更新
      setTimeout(() => {
        window.dispatchEvent(new Event('navItemsUpdated'));
      }, 0);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNavItemsFromApi.pending, (state) => {
        logger.log('【导航日志】fetchNavItemsFromApi.pending - 开始从API获取导航项');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNavItemsFromApi.fulfilled, (state, action) => {
        logger.log('【导航日志】fetchNavItemsFromApi.fulfilled - API请求成功', action.payload);
        if (action.payload) {
          logger.log('【导航日志】更新导航项状态，项目数量:', action.payload.length);
          state.navItems = action.payload;
          state.loading = false;
          state.error = null;
        } else {
          logger.log('【导航日志】API返回了空数据');
        }
      })
      .addCase(fetchNavItemsFromApi.rejected, (state, action) => {
        logger.log('【导航日志】fetchNavItemsFromApi.rejected - API请求失败', action.error);
        state.loading = true;
      })
      .addCase(loadNavItems.pending, (state) => {
        logger.log('【导航日志】loadNavItems.pending - 开始加载导航项');
        state.loading = true;
        state.error = null;
      })
      .addCase(loadNavItems.fulfilled, (state, action) => {
        logger.log('【导航日志】loadNavItems.fulfilled - 加载导航项成功', action.payload);
        logger.log('【导航日志】更新状态中的导航项，数量:', action.payload.length);
        state.navItems = action.payload;
        state.loading = false;
        state.error = null;
        
        // 使用防抖函数触发事件
        debounceDispatchEvent('navItemsUpdated', { source: 'loadNavItems' });
      })
      .addCase(loadNavItems.rejected, (state, action) => {
        logger.error('【导航日志】loadNavItems.rejected - 加载导航项失败', action.error);
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loadNavItemsOptimized.pending, (state) => {
        logger.log('【导航日志】loadNavItemsOptimized.pending - 开始优化加载导航项');
        state.loading = true;
        state.error = null;
      })
      .addCase(loadNavItemsOptimized.fulfilled, (state, action) => {
        logger.log('【导航日志】loadNavItemsOptimized.fulfilled - 优化加载导航项成功', action.payload);
        logger.log('【导航日志】更新状态中的导航项，数量:', action.payload.length);
        state.navItems = action.payload;
        state.loading = false;
        state.error = null;
        
        // 使用防抖函数触发事件
        debounceDispatchEvent('navItemsUpdated', { source: 'loadNavItemsOptimized' });
      })
      .addCase(loadNavItemsOptimized.rejected, (state, action) => {
        logger.error('【导航日志】loadNavItemsOptimized.rejected - 优化加载导航项失败', action.error);
        state.loading = false;
        state.error = action.payload;
      })
      // 处理添加导航项
      .addCase(addNavItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNavItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        logger.log('【导航日志】addNavItemAsync.fulfilled - 添加导航项成功', action.payload);
        
        // 触发自定义事件，通知其他组件导航项已添加
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('navItemsUpdated', {
            detail: {
              isNewNav: true,
              newNavId: action.payload.newItem?.id
            }
          }));
        }, 0);
      })
      .addCase(addNavItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        logger.error('【导航日志】addNavItemAsync.rejected - 添加导航项失败', action.error);
      })
      // 处理更新导航项
      .addCase(updateNavItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNavItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        logger.log('【导航日志】updateNavItemAsync.fulfilled - 更新导航项成功', action.payload);
      })
      .addCase(updateNavItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        logger.error('【导航日志】updateNavItemAsync.rejected - 更新导航项失败', action.error);
      })
      // 处理重排序导航项
      .addCase(reorderNavItemsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reorderNavItemsAsync.fulfilled, (state, action) => {
        state.loading = false;
        logger.log('【导航日志】reorderNavItemsAsync.fulfilled - 重排序导航项成功', action.payload);
        
        // 触发自定义事件，通知其他组件导航项已重排序
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('navItemsUpdated', {
            detail: {
              isReorder: true,
              sourceIndex: action.payload.sourceIndex,
              targetIndex: action.payload.targetIndex
            }
          }));
        }, 0);
      })
      .addCase(reorderNavItemsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        logger.error('【导航日志】reorderNavItemsAsync.rejected - 重排序导航项失败', action.error);
      })
      // 处理删除导航项
      .addCase(deleteNavItemAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNavItemAsync.fulfilled, (state, action) => {
        state.loading = false;
        logger.log('【导航日志】deleteNavItemAsync.fulfilled - 删除导航项成功', action.payload);
      })
      .addCase(deleteNavItemAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        logger.error('【导航日志】deleteNavItemAsync.rejected - 删除导航项失败', action.error);
      });
  },
});

export const { 
  setNavItems, 
  addNavItem, 
  updateNavItem, 
  reorderNavItems 
} = navSlice.actions;

export default navSlice.reducer; 